import TabBox from "../../Components/Tabbox/TabBox";
import {
  IFundOrderTableSearchParams,
  IManualTablesSearchParam,
} from "../../Interfaces/manualInterfaces";
import {
  getManualErrorsTable,
  getManualFailedOrderTable,
  markManualFailedOrderResolved,
  previewManualFailedOrderResend,
  previewManualFailedOrderResendBulk,
  resendManualFailedOrdersBulk,
  sendManualFailedOrderResend,
} from "../../Requests/manualRequests";
import ManualOrderTable from "../../Tables/Manual/Order/Table";
import ManualTradeTable from "../../Tables/Manual/Trade/TradeTable";
import ManualFundOrderTable from "../../Tables/Manual/FundOrder/FundOrderTable";
import ErrorsTable from "../../Tables/Order/ErrorsTable/ErrorsTable";
import FailedOrdersTable from "../../Tables/Order/FailedOrdersTable/FailedOrdersTable";
import PreviewManualOrderTable from "../../Tables/Manual/PreviewManualOrderTable";
import { OrderTablesTabIndex } from "../Order/OrderTablesBox/OrderResult";
import AddOrderBox from "./AddOrderBox/AddOrderBox";
import { useCallback, useMemo, useState } from "react";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";
import SearchParamBox, { ManualFilterMode } from "./SearchParamBox";
import OrderBook from "./OrderBook";
import EditOrder from "./EditOrder";

enum ManualTablesTabIndex {
  OrderTable = OrderTablesTabIndex.OrderTable,
  TradeTable = OrderTablesTabIndex.TradeTable,
  ErrorsTable = OrderTablesTabIndex.ErrorsTable,
  FundOrderTable = 5,
  FailedOrdersTable = 6,
}

const Manual = () => {
  const [tabIndex, setTabIndex] = useState(ManualTablesTabIndex.OrderTable);

  const [tablesSearchParam, setTablesSearchParam] =
    useState<IManualTablesSearchParam>({} as IManualTablesSearchParam);
  const [fundInstrumentId, setFundInstrumentId] = useState<string | null>(null);
  const fundOrderSearchParam = useMemo<IFundOrderTableSearchParams>(() => ({
    portfolioGroupId: tablesSearchParam.portfolioGroupId,
    sahraUserId: tablesSearchParam.sahraUserId,
    instrumentId: fundInstrumentId,
  }), [tablesSearchParam.portfolioGroupId, tablesSearchParam.sahraUserId, fundInstrumentId]);

  const { errorTabCount, errorTableRefreshFlag, errorTableAfterFetchHandler } =
    useErrorTabCount(
      "manual",
      tabIndex,
      ManualTablesTabIndex.ErrorsTable,
      getManualErrorsTable
    );

  const onTablesSearchParamChange = useCallback((name: string, value: any) => {
    setTablesSearchParam((previous) => ({ ...previous, [name]: value }));
  }, []);

  const onFundOrderSearchParamChange = useCallback((name: string, value: any) => {
    const normalizedValue = value === "" || value == null ? null : value;
    if (name === "instrumentId") {
      setFundInstrumentId(normalizedValue);
    } else {
      onTablesSearchParamChange(name, normalizedValue);
    }
  }, [onTablesSearchParamChange]);

  const tablesTabsChildren = [
    { index: ManualTablesTabIndex.OrderTable, title: "سفارشات" },
    { index: ManualTablesTabIndex.TradeTable, title: "معاملات" },
    { index: ManualTablesTabIndex.FundOrderTable, title: "معاملات صندوق" },
    {
      index: ManualTablesTabIndex.FailedOrdersTable,
      title: "سفارش‌های ناموفق",
    },
    {
      index: ManualTablesTabIndex.ErrorsTable,
      title: "خطا",
      count: errorTabCount,
    },
  ];

  const getTabIndexTable = () => {
    switch (tabIndex) {
      case ManualTablesTabIndex.OrderTable:
        return <ManualOrderTable searchParam={tablesSearchParam} />;

      case ManualTablesTabIndex.TradeTable:
        return <ManualTradeTable searchParam={tablesSearchParam} />;

      case ManualTablesTabIndex.FundOrderTable:
        return <ManualFundOrderTable searchParam={fundOrderSearchParam} />;

      case ManualTablesTabIndex.ErrorsTable:
        return (
          <ErrorsTable
            getData={getManualErrorsTable}
            searchParams={tablesSearchParam}
            onFetchComplete={errorTableAfterFetchHandler}
            refreshFlag={errorTableRefreshFlag}
          />
        );

      case ManualTablesTabIndex.FailedOrdersTable:
        return (
          <FailedOrdersTable
            getData={getManualFailedOrderTable}
            previewResend={previewManualFailedOrderResend}
            sendOrder={sendManualFailedOrderResend}
            markResolved={markManualFailedOrderResolved}
            renderPreviewRows={(rows) => <PreviewManualOrderTable values={rows} />}
            previewResendBulk={previewManualFailedOrderResendBulk}
            resendBulk={resendManualFailedOrdersBulk}
            searchParams={tablesSearchParam}
          />
        );
    }
  };

  const getFilterMode = (): ManualFilterMode => {
    switch (tabIndex) {
      case ManualTablesTabIndex.FundOrderTable:
        return "fund";
      case ManualTablesTabIndex.TradeTable:
        return "trade";
      case ManualTablesTabIndex.ErrorsTable:
        return "errors";
      case ManualTablesTabIndex.FailedOrdersTable:
        return "failedOrders";
      default:
        return "order";
    }
  };

  return (
    <div className="flex flex-row justify-start items-start gap-5 w-full pr-5">
      <AddOrderBox />
      <div className="flex flex-col justify-start items-start">
        <div className="flex flex-row justify-start items-start w-full">
          <OrderBook />
        </div>
        <EditOrder />
        <div className="w-full pt-5">
          <div className="max-w-min m-auto">
            <div className="w-full flex flex-row justify-start items-center gap-3">
              <div className="max-w-min shrink-0">
                <TabBox
                  activeTab={tabIndex}
                  children={tablesTabsChildren}
                  onChange={(name, index) => setTabIndex(index)}
                />
              </div>
              <SearchParamBox
                onChange={onTablesSearchParamChange}
                searchParam={tablesSearchParam}
                mode={getFilterMode()}
                fundSearchParam={fundOrderSearchParam}
                onFundChange={onFundOrderSearchParamChange}
              />
            </div>
            <div className="w-full mt-2">{getTabIndexTable()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manual;
